/**
 * The Quality Data Package as the customer reads and prints it. CR-2026-QDP (2026-10-14). SFM-SPEC-QDP-001 §8.
 * Renders only what the server froze in the package content. A gap shows as an empty red cell naming the record it
 * needs; nothing is filled in. Anything not issued carries a DRAFT banner on every page.
 * CR-2026-QDP-NDE (2026-10-14): Section 4 weld map and Section 5.3 NDE, shown when the content carries a weld map
 * (packages built before this change do not, and show as before).
 */
import React from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { section10, verifyUrl } from "./qdpModel";

const when = (d) => (d ? String(d).slice(0, 10) : "");
const cell = { border: "1px solid #c9d1dc", fontSize: 11, py: 0.5, px: 0.75, verticalAlign: "top" };
const gapCell = { ...cell, bgcolor: "#FBE9E9", color: "#B02020", fontWeight: 600 };
const head = { ...cell, bgcolor: "#1C2636", color: "#fff", fontWeight: 600 };

function Section({ n, title, children }) {
  return (
    <Box className="qdp-section" sx={{ mt: 3, breakInside: "avoid-page" }}>
      <Typography sx={{ fontSize: 11, letterSpacing: 1.5, color: "#555F6B" }}>SECTION {n}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1C2636", mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

export default function QdpDocument({ pkg }) {
  const c = pkg.content;
  const issued = ["ISSUED", "SUPERSEDED"].includes(pkg.state);
  const fp = (issued ? pkg.packageSha256 : pkg.contentSha256) || "";
  const footer = `${pkg.number} Rev ${pkg.revision} · ${issued ? "fingerprint" : "draft content"} ${fp.slice(0, 12)}`;
  const s10 = section10(c, pkg.exceptions);
  const heatById = new Map(c.heats.map((h) => [h.heatId, h]));
  return (
    <Box className="qdp-doc" sx={{ bgcolor: "#fff", color: "#141A24", p: { xs: 1, md: 3 }, fontFamily: "'IBM Plex Sans', Roboto, sans-serif" }}>
      {!issued && <Box sx={{ bgcolor: "#F88F1D", textAlign: "center", fontWeight: 700, fontSize: 12, py: 0.5, mb: 2 }}>DRAFT · NOT ISSUED · NOT A QUALITY RECORD</Box>}
      {pkg.state === "SUPERSEDED" && <Box sx={{ bgcolor: "#EEF1F5", textAlign: "center", fontWeight: 700, fontSize: 12, py: 0.5, mb: 2 }}>SUPERSEDED BY A LATER REVISION</Box>}

      <Box sx={{ bgcolor: "#1C2636", color: "#fff", p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 12, letterSpacing: 2, opacity: 0.8 }}>QUALITY DATA PACKAGE</Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 600, fontFamily: "monospace" }}>{pkg.number} Rev {pkg.revision}</Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.85 }}>{c.job.name} · {c.scope === "FINAL" ? "Final package (job closeout)" : `${c.scope === "FLOOR" ? "Floor" : "Sequence"} ${c.group.code}${c.group.name ? ` (${c.group.name})` : ""}`}</Typography>
        </Box>
        {issued && pkg.verifyToken && (
          <Box sx={{ bgcolor: "#fff", p: 1, textAlign: "center" }}>
            <QRCodeSVG value={verifyUrl(pkg.verifyToken)} size={96} />
            <Typography sx={{ fontSize: 9, color: "#555F6B" }}>Scan to verify</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ border: "1.5px solid #1C2636", mt: 2 }}>
        <Box sx={{ bgcolor: "#EEF1F5", px: 2, py: 1, fontWeight: 700, fontSize: 12, letterSpacing: 1 }}>CERTIFICATE OF CONFORMANCE</Box>
        <Typography sx={{ p: 2, fontSize: 13, lineHeight: 1.6 }}>{pkg.cocText || "Certificate wording is set when the Quality Manager approves the package."}</Typography>
        <Box sx={{ px: 2, pb: 2, display: "flex", gap: 4, flexWrap: "wrap", fontSize: 12 }}>
          <span>Quality Manager: <strong>{pkg.approvedByName || "not yet approved"}</strong></span>
          <span>Approved: <strong>{when(pkg.approvedAt) || "-"}</strong></span>
          <span>Technical review: <strong>{pkg.reviewedByName ? `${pkg.reviewedByName}, ${when(pkg.reviewedAt)}` : "not yet reviewed"}</strong></span>
          <span>Issued: <strong>{when(pkg.issuedAt) || "-"}</strong></span>
        </Box>
      </Box>

      <Section n="1" title="Contract requirements">
        <Typography sx={{ fontSize: 12 }}>Material traceability required by contract: <strong>{c.job.traceabilityLevel}</strong>{c.job.contractRef ? ` (${c.job.contractRef})` : ""}. {c.traceabilityStatement}</Typography>
      </Section>

      {c.scope === "FINAL" ? (
        <Section n="2" title="Build-group packages in this final package">
          <Table size="small"><TableHead><TableRow>{["Group", "Pieces", "Package", "Issued", "Fingerprint (SHA-256)"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{(c.groups || []).map((g) => {
              const k = (c.packages || []).find((x) => x.groupCode === g.code);
              return (
                <TableRow key={g.code}>
                  <TableCell sx={{ ...cell, fontWeight: 600 }}>{g.code}{g.name ? ` (${g.name})` : ""}</TableCell><TableCell sx={cell}>{g.pieces}</TableCell>
                  <TableCell sx={k ? cell : gapCell}>{k ? `${k.number} Rev ${k.revision}` : "GAP: no issued package"}</TableCell>
                  <TableCell sx={cell}>{k ? when(k.issuedAt) : ""}</TableCell>
                  <TableCell sx={{ ...cell, fontFamily: "monospace", fontSize: 9 }}>{k ? k.packageSha256 : ""}</TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        </Section>
      ) : (
      <Section n="2" title={`Traceability matrix · ${c.group.code}`}>
        <Table size="small" sx={{ borderCollapse: "collapse" }}>
          <TableHead><TableRow>{["Piece", "Identity", "Heat", "MTR", "Welding", "Visual (QCI)", "Coating DFT", "NCR", "Load"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {c.pieces.map((p) => {
              const mtrOk = p.heats.length && p.heats.every((h) => (heatById.get(h.heatId)?.mtr || []).length);
              return (
                <TableRow key={p.pieceId}>
                  <TableCell sx={{ ...cell, fontWeight: 600 }}>{p.mark} {p.display}</TableCell>
                  <TableCell sx={{ ...cell, fontFamily: "monospace" }}>{p.publicId}</TableCell>
                  <TableCell sx={p.heats.length ? cell : gapCell}>{p.heats.length ? p.heats.map((h) => `${h.heatNumber}${h.level === "MARK" ? " (mark)" : ""}${h.confirmedAtCut ? " ✓cut" : ""}`).join(", ") : "GAP: heat link required"}</TableCell>
                  <TableCell sx={mtrOk ? cell : gapCell}>{mtrOk ? "Linked (S3)" : "GAP: MTR"}</TableCell>
                  <TableCell sx={cell}>{p.welds.length ? p.welds.map((w) => `${w.wps || "WPS ?"} / ${w.welder || "?"}`).join("; ") : "None recorded"}</TableCell>
                  <TableCell sx={p.visual.length ? cell : gapCell}>{p.visual.length ? p.visual.map((v) => `${v.station}: ${v.approvedBy} ${when(v.at)}`).join("; ") : "GAP: QCI inspection"}</TableCell>
                  <TableCell sx={cell}>{p.dft.length ? p.dft.map((d) => `${d.layer} ${d.measured} (${d.min}-${d.max})${d.withinSpec ? "" : " OUT"}`).join("; ") : "None recorded"}</TableCell>
                  <TableCell sx={cell}>{p.ncrs.length ? p.ncrs.map((n) => `${n.ncrNumber} ${n.status}`).join("; ") : "None"}</TableCell>
                  <TableCell sx={p.loads.length ? cell : gapCell}>{p.loads.length ? p.loads.map((l) => `${l.loadNumber} ${l.state}`).join("; ") : "GAP: not shipped"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Section>
      )}

      {c.scope !== "FINAL" && (
      <Section n="3" title="Material: heats and MTRs">
        <Table size="small"><TableHead><TableRow>{["Heat", "Grade / spec", "Supplier", "Status", "Released by", "MTR / CMTR", "File SHA-256"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>{c.heats.map((h) => (
            <TableRow key={h.heatId}>
              <TableCell sx={{ ...cell, fontWeight: 600 }}>{h.heatNumber}</TableCell><TableCell sx={cell}>{[h.grade, h.materialSpec].filter(Boolean).join(" / ")}</TableCell>
              <TableCell sx={cell}>{h.supplier || ""}</TableCell><TableCell sx={h.status === "RELEASED" ? cell : gapCell}>{h.status}</TableCell>
              <TableCell sx={cell}>{h.releasedBy ? `${h.releasedBy} ${when(h.releasedAt)}` : ""}</TableCell>
              <TableCell sx={h.mtr.length ? cell : gapCell}>{h.mtr.length ? h.mtr.map((m) => m.name).join("; ") : "GAP: MTR record"}</TableCell>
              <TableCell sx={{ ...cell, fontFamily: "monospace", fontSize: 9 }}>{h.mtr.map((m) => (m.sha256 ? m.sha256.slice(0, 16) : "not stored")).join("; ")}</TableCell>
            </TableRow>))}
          </TableBody>
        </Table>
      </Section>
      )}

      {c.scope !== "FINAL" && Array.isArray(c.weldMap) && (
      <Section n="4" title="Welding records: weld map">
        {c.weldMap.every((m) => !m.welds.length) ? <Typography sx={{ fontSize: 12 }}>None recorded.</Typography> : (
          <Table size="small"><TableHead><TableRow>{["Mark", "Joint", "Category and NDE plan", "WPS / process", "Welder, date", "Welder qualification", "WPS match", "Status"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{c.weldMap.flatMap((m) => m.welds.map((w) => {
              const cat = w.category || {};
              const plan = (cat.methods || []).length ? ` NDE: ${cat.methods.join(", ")} ${cat.extentType === "full" ? `${cat.extentPct}%` : `${cat.extentPct ? `${cat.extentPct}% ` : ""}${cat.extentType || ""}`}` : "";
              const qualBad = w.qualification == null || ["expired", "no_record", "continuity_lapsed"].includes(w.qualification);
              return (
                <TableRow key={`${m.itemId}-${w.record}`}>
                  <TableCell sx={{ ...cell, fontWeight: 600 }}>{m.mark}</TableCell>
                  <TableCell sx={cell}>{w.joint || `record ${w.record} (no joint ID)`}</TableCell>
                  <TableCell sx={cat.label ? cell : gapCell}>{cat.label ? `${cat.code} ${cat.label} (${cat.classification}).${plan}` : `${cat.code}: no category plan`}</TableCell>
                  <TableCell sx={cell}>{[w.wps || "WPS not recorded", w.process].filter(Boolean).join(" / ")}</TableCell>
                  <TableCell sx={cell}>{[w.welder || "welder not recorded", w.date].filter(Boolean).join(", ")}</TableCell>
                  <TableCell sx={qualBad ? gapCell : cell}>{w.qualification || "not evaluated"}</TableCell>
                  <TableCell sx={w.wpsMatch == null || w.wpsMatch === "mismatch" ? gapCell : cell}>{w.wpsMatch || "not evaluated"}</TableCell>
                  <TableCell sx={w.status === "complete" ? cell : gapCell}>{w.status}</TableCell>
                </TableRow>
              );
            }))}</TableBody>
          </Table>
        )}
        <Typography sx={{ fontSize: 11, color: "#555F6B", mt: 1 }}>Weld records are kept per mark, not per piece. This is a table of recorded joints, not a weld location drawing.</Typography>
      </Section>
      )}

      {c.scope !== "FINAL" && Array.isArray(c.weldMap) && (
      <Section n="5" title="Inspections: nondestructive examination">
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>5.3 Nondestructive examination (NDE) of welds</Typography>
        {c.weldMap.every((m) => m.welds.every((w) => !w.nde.length)) ? <Typography sx={{ fontSize: 12 }}>None recorded.</Typography> : (
          <Table size="small"><TableHead><TableRow>{["Mark / joint", "Method", "Extent", "Result", "Report", "Technician", "Certification on record covering the date", "Inspected", "Acceptance basis"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{c.weldMap.flatMap((m) => m.welds.flatMap((w) => w.nde.map((n, i) => {
              if (n.status === "voided") {
                const v = n.voided || {};
                return (
                  <TableRow key={`${m.itemId}-${w.record}-${i}`}>
                    <TableCell sx={{ ...cell, fontWeight: 600 }}>{m.mark} {w.joint || `record ${w.record}`}</TableCell>
                    <TableCell sx={cell}>{n.method}</TableCell><TableCell sx={cell}>{String(n.extent || "").replace(/_/g, " ")}</TableCell>
                    <TableCell sx={n.result === "rejected" ? gapCell : cell}>VOIDED (was {n.result})</TableCell>
                    <TableCell sx={cell}>{n.reportNumber || ""}</TableCell><TableCell sx={cell}>{n.technician || ""}</TableCell>
                    <TableCell sx={cell}>{`Voided${v.by ? ` by ${v.by}` : ""}${v.at ? ` ${when(v.at)}` : ""}: ${v.reason || "no reason recorded"}`}</TableCell>
                    <TableCell sx={cell}>{n.inspectedDate || ""}</TableCell><TableCell sx={cell}>{n.acceptanceBasis || ""}</TableCell>
                  </TableRow>
                );
              }
              const done = n.status === "complete" && ["accepted", "rejected"].includes(n.result); const cert = n.certification; const needReport = done && n.method !== "VT" && !n.reportNumber;
              return (
                <TableRow key={`${m.itemId}-${w.record}-${i}`}>
                  <TableCell sx={{ ...cell, fontWeight: 600 }}>{m.mark} {w.joint || `record ${w.record}`}</TableCell>
                  <TableCell sx={cell}>{n.method}</TableCell><TableCell sx={cell}>{String(n.extent || "").replace(/_/g, " ")}</TableCell>
                  <TableCell sx={done && n.result === "accepted" ? cell : gapCell}>{done ? `${n.result}${n.indications ? ` (${n.indications} indications)` : ""}` : `GAP: ${n.status}, result ${n.result}`}</TableCell>
                  <TableCell sx={needReport ? gapCell : cell}>{n.reportNumber || (needReport ? "GAP: no report number" : "")}</TableCell>
                  <TableCell sx={cell}>{n.technician || (done ? "not recorded" : "")}</TableCell>
                  <TableCell sx={!done || (cert && cert.qualifies !== false) ? cell : gapCell}>{!done ? "" : !n.inspectedDate ? "GAP: no inspection date" : cert ? `${cert.type}${cert.number ? ` ${cert.number}` : ""}${cert.issuedBy ? `, ${cert.issuedBy}` : ""}${cert.expires ? `, expires ${cert.expires}` : ""}${cert.qualifies === false ? " (not CWI or SCWI; basis declared in Section 10)" : ""}` : "GAP: no certification on record for this method on the date"}</TableCell>
                  <TableCell sx={cell}>{n.inspectedDate || ""}</TableCell><TableCell sx={cell}>{n.acceptanceBasis || ""}</TableCell>
                </TableRow>
              );
            })))}</TableBody>
          </Table>
        )}
        <Typography sx={{ fontSize: 11, color: "#555F6B", mt: 1 }}>A technician is shown as qualified for a method only when a certification on record in SFM covers the inspection date: VT by CWI or SCWI; MT by MT Level II; PT by PT Level II; UT by UT Level II or III; RT by RT Level II. A VT by an AWS-CAWI is printed as such, with the supervising CWI the Quality Manager declared in Section 10. SFM does not record which certification was presented at the inspection.</Typography>
        {(c.ndeSampling || []).length > 0 && (
          <>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 2, mb: 0.5 }}>5.4 NDE by sampling (random or spot plans) in this package</Typography>
            <Table size="small"><TableHead><TableRow>{["Weld category", "Plan", "Welds in this package", "Welds tested (accepted)"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>{c.ndeSampling.map((x) => (
                <TableRow key={x.category}><TableCell sx={{ ...cell, fontWeight: 600 }}>{x.category}</TableCell><TableCell sx={cell}>{x.plan}</TableCell>
                  <TableCell sx={cell}>{x.welds}</TableCell><TableCell sx={x.tested ? cell : gapCell}>{x.tested}</TableCell></TableRow>))}
              </TableBody>
            </Table>
            <Typography sx={{ fontSize: 11, color: "#555F6B", mt: 1 }}>Whether the sampling rate was met across the whole job is not assessed in this package.</Typography>
          </>
        )}
      </Section>
      )}

      <Section n="7" title="NCR summary">
        {c.ncrSummary.length === 0 ? <Typography sx={{ fontSize: 12 }}>None.</Typography> : (
          <Table size="small"><TableHead><TableRow>{["NCR", "Pieces", "Description", "Gap type", "Disposition", "Status", "Closed"].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{c.ncrSummary.map((n) => (
              <TableRow key={`${n.register}-${n.ncrNumber}`}>
                <TableCell sx={{ ...cell, fontFamily: "monospace" }}>{n.ncrNumber}</TableCell><TableCell sx={cell}>{n.pieces.join(", ")}{n.linkLevel !== "PIECE" ? ` (${n.linkLevel.toLowerCase().replace("_", " ")})` : ""}</TableCell>
                <TableCell sx={cell}>{n.description}</TableCell><TableCell sx={cell}>{n.gapType || ""}</TableCell><TableCell sx={cell}>{n.disposition || ""}</TableCell>
                <TableCell sx={cell}>{n.status}</TableCell><TableCell sx={cell}>{when(n.closedAt)}</TableCell>
              </TableRow>))}
            </TableBody>
          </Table>
        )}
        <Typography sx={{ fontSize: 11, color: "#555F6B", mt: 1 }}>{c.ncrNote}</Typography>
      </Section>

      <Section n="10" title="Declared exceptions, disclosures and limitations">
        {s10.length === 0 ? <Typography sx={{ fontSize: 12 }}>None.</Typography> : (
          <Table size="small"><TableBody>{s10.map((l, i) => (
            <TableRow key={i}><TableCell sx={{ ...cell, fontWeight: 600, width: 110 }}>{l.kind}</TableCell><TableCell sx={{ ...cell, fontFamily: "monospace", width: 220 }}>{l.text}</TableCell><TableCell sx={cell}>{l.detail}</TableCell></TableRow>))}
          </TableBody></Table>
        )}
      </Section>

      <Box sx={{ mt: 3, pt: 1, borderTop: "1px solid #EEF1F5", fontFamily: "monospace", fontSize: 10, color: "#555F6B" }}>{footer}</Box>
    </Box>
  );
}
